import {
  Customer,
  ExtractFlightDateWorkerEventPayload,
  FlightDateExtractedEventPayload,
  ScanDate,
  ScheduledFlight,
  Site,
} from '../../../../../../types';
import * as React from 'react';
import { substrDateFromAirtableFormat } from '../../../helpers/date';
import confirmFlightDate, { DateDiscrepancyReason } from '../confirmFlightDate/confirmFlightDate';
import { resolveConfirmDateBounds } from '../confirmFlightDate/confirmDateBounds';
import { UploadType } from '../../../../../../consts/uploadType';
import { createCanceledError } from '../../../../../../errors/createCanceledError';
import { FileWithPath } from 'react-dropzone';
import { mapUploadTypeToMetricsEntityType } from '../../../../../../libs/metrics/metricUtils';
import { Point } from '@influxdata/influxdb-client-browser';
import { influxWriter } from '../../../../../../libs/metrics/InfluxWriter';
import { getTimezoneFromCoordinates } from '../../../../../../libs/getTimezoneFromCoordinates';
import { differenceInCalendarDays } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { makeSafeIsoDateString } from '../../../../../../libs/doneDate';

export type ConfirmedUploadDate = {
  /** The date the upload is created on — always the scheduled one. */
  date: ScanDate;
  /**
   * The date the pilot stated in the modal. Becomes the upload's `exifDate` at mark-loaded, taking
   * precedence over the photos. Undefined when no modal fired, or when it was a warning only.
   */
  pilotConfirmedDate?: ScanDate;
  /**
   * What the per-photo `photoAgeHours` check measures each photo against: the date the dataset itself
   * claims, read from the first image. Sent only alongside `pilotConfirmedDate`.
   *
   * Not `pilotConfirmedDate` itself. Both share the five-day threshold, and the pilot is only asked
   * when the drone's clock could not be verified, so measuring the photos against their answer would
   * fail all of them. Against the dataset's own date the check still catches a photo that does not
   * belong with the rest.
   *
   * Undefined on every other upload, leaving the scheduled date as the baseline — the only form of the
   * check that catches a whole dataset belonging to another date. For scans and panos alike that verdict
   * reaches ops as `DateRange_Check (a)` / `PanoDateRange_Check (a)` on `3. Scans`, via `dateRangeCheck`
   * in `pilot-api`'s validation summary.
   */
  expectedPhotoDate?: ScanDate;
};

export const confirmUploadDate = async (
  signal: AbortSignal,
  uploadType: UploadType,
  customer: Customer,
  site: Site,
  schedule: ScheduledFlight,
  files: FileWithPath[],
  showWindow: (window: React.ReactNode) => void,
  hideWindow: (window: React.ReactNode) => void,
): Promise<ConfirmedUploadDate> => {
  const scheduledDate = substrDateFromAirtableFormat(schedule.flightDateIso);

  const firstImageFile = files.find((image) => image.type?.includes('image/') && image.size >= 1024);
  if (!firstImageFile) {
    shipFlightDateResolverFailMetrics(customer.id, site.id, uploadType, scheduledDate);
    return { date: scheduledDate };
  }

  const timezone = await getTimezoneFromCoordinates(customer.id + '/' + site.id, site.boundary);

  // Local date (YYYY-MM-DD) in the site's timezone, plus whether it came from an absolute timestamp
  // or from the drone's own clock.
  const { flightDate: exifDate, precise } = await extractExifDate(firstImageFile, timezone);
  if (signal?.aborted) throw createCanceledError();

  if (!exifDate) {
    shipFlightDateResolverFailMetrics(customer.id, site.id, uploadType, scheduledDate);
    return { date: scheduledDate };
  }

  // The browser clock is taken as correct, read as a date in the site's timezone rather than its own.
  const today = formatInTimeZone(new Date(), timezone, 'yyyy-MM-dd');
  const isFromFuture = exifDate > today;

  const daysDiff = Math.abs(
    differenceInCalendarDays(new Date(makeSafeIsoDateString(exifDate)), new Date(makeSafeIsoDateString(scheduledDate))),
  );

  // A GPS date is only queried when it is far off the schedule; the drone's own clock on any mismatch.
  const maxAllowedDateDiffDays = Validation.config.photoCreationTimeToNowRangeHours / 24;
  const isOffSchedule = precise ? daysDiff > maxAllowedDateDiffDays : exifDate !== scheduledDate;
  const reason = isFromFuture ? 'future' : 'offSchedule';

  let pilotConfirmedDate: ScanDate | undefined;

  if (isOffSchedule || isFromFuture) {
    let outcome: ConfirmationOutcome = 'Cancelled';
    // The date the picker opens on, recomputed from the same two dates the modal derives it from.
    const prefilledDate = resolveConfirmDateBounds(scheduledDate, today).initial;

    try {
      pilotConfirmedDate = await confirmFlightDate({
        showWindow,
        hideWindow,
        photoDate: exifDate,
        scheduledDate,
        today,
        daysDiff,
        reason,
        precise,
        site,
      });
      outcome = !pilotConfirmedDate ? 'Acknowledged' : pilotConfirmedDate === prefilledDate ? 'Confirmed' : 'Changed';
    } finally {
      // `Confirmed` vs `Changed` is measured against the prefill, not the photo date: it is there to show
      // whether pilots read the question or just accept whatever the picker opened on.
      shipFlightDateConfirmationMetrics(customer.id, site.id, uploadType, {
        outcome,
        reason,
        precise,
        photoDate: exifDate,
        scheduledDate,
        confirmedDate: pilotConfirmedDate,
        daysDiff,
      });
    }
  }

  // Also shipped for a future date that agrees with the schedule, which the comparison alone misses.
  if (exifDate !== scheduledDate || isFromFuture) {
    shipFlightDateResolverMetrics(
      customer.id,
      site.id,
      uploadType,
      exifDate,
      scheduledDate,
      daysDiff,
      precise,
      isFromFuture,
    );
  }

  // The upload is created on the scheduled date for both types; the other two dates travel alongside it
  // and are persisted by `pilot-api`, so a reload does not lose them. Creating it on an off-schedule
  // date instead would hide a scan from `useRestoreUploads` and break the schedule date that
  // `updateScheduledFlight` derives from the upload's own.
  return {
    date: scheduledDate,
    pilotConfirmedDate,
    expectedPhotoDate: pilotConfirmedDate ? exifDate : undefined,
  };
};

export const extractExifDate = (file: File, timezone: string): Promise<FlightDateExtractedEventPayload> => {
  return new Promise((resolve) => {
    const unresolved: FlightDateExtractedEventPayload = { flightDate: undefined, precise: false };

    if (typeof Worker === 'undefined') {
      console.warn('Web Workers are not supported in this browser');
      resolve(unresolved);
      return;
    }

    try {
      const message: ExtractFlightDateWorkerEventPayload = {
        messageType: 'extractFlightDate',
        photo: file,
        timezone,
      };

      const worker = new Worker('./js/intakeWorker/index.js');

      worker.addEventListener('error', (error) => {
        clearTimeout(timeoutId);
        worker.terminate();
        console.warn('Worker error:', error);
        resolve(unresolved);
      });

      const timeoutId = setTimeout(() => {
        worker.terminate();
        console.warn('Worker timed out');
        resolve(unresolved);
      }, 30_000);

      const onmessage = (e) => {
        if (e.data.eventType === 'flightDateExtracted') {
          clearTimeout(timeoutId);
          resolve(e.data.payload as FlightDateExtractedEventPayload);
          worker.terminate();
        } else {
          clearTimeout(timeoutId);
          worker.terminate();
          console.warn('Unexpected message from worker');
          resolve(unresolved);
        }
      };

      worker.addEventListener('message', onmessage, { once: true });
      worker.postMessage(message);
    } catch (error) {
      console.warn('Error creating or using worker:', error);
      resolve(unresolved);
    }
  });
};

/** What the pilot did when asked. `Acknowledged` is the GPS window, which has no date input. */
type ConfirmationOutcome = 'Confirmed' | 'Changed' | 'Acknowledged' | 'Cancelled';

const shipFlightDateConfirmationMetrics = (
  customerId: string,
  siteId: string,
  uploadType: UploadType,
  result: {
    outcome: ConfirmationOutcome;
    reason: DateDiscrepancyReason;
    precise: boolean;
    photoDate: string;
    scheduledDate: string;
    confirmedDate?: string;
    daysDiff: number;
  },
) => {
  const point = new Point();
  point.tag('Customer', customerId);
  point.tag('Site', siteId);
  point.tag('EntityType', mapUploadTypeToMetricsEntityType(uploadType));
  point.tag('Outcome', result.outcome);
  point.tag('Reason', result.reason);
  point.tag('HasGpsDate', String(result.precise));

  point.floatField('value', 1);
  point.stringField('PhotoDate', result.photoDate);
  point.stringField('ScheduledDate', result.scheduledDate || '');
  point.stringField('ConfirmedDate', result.confirmedDate || '');
  point.intField('DaysDiff', result.daysDiff);

  influxWriter.send('FlightDateConfirmationResult', point);
};

const shipFlightDateResolverMetrics = (
  customerId: string,
  siteId: string,
  uploadType: UploadType,
  exifDate: string,
  scheduledDate: string,
  daysDiff: number,
  precise: boolean,
  fromFuture: boolean,
) => {
  const point = new Point();
  point.tag('Customer', customerId);
  point.tag('Site', siteId);
  point.tag('EntityType', mapUploadTypeToMetricsEntityType(uploadType));
  // Named `HasGpsDate` rather than `Precise`: an offset-bearing date is well-formed but still unverified.
  point.tag('HasGpsDate', String(precise));
  point.tag('FromFuture', String(fromFuture));

  point.floatField('value', 1);
  point.stringField('ExifDate', exifDate);
  point.stringField('ScheduledDate', scheduledDate || '');
  point.intField('DaysDiff', daysDiff);

  influxWriter.send('FlightDateResolverResult', point);
};

const shipFlightDateResolverFailMetrics = (
  customerId: string,
  siteId: string,
  uploadType: UploadType,
  scheduledDate: string,
) => {
  const point = new Point();
  point.tag('Customer', customerId);
  point.tag('Site', siteId);
  point.tag('EntityType', mapUploadTypeToMetricsEntityType(uploadType));

  point.floatField('count', 1);
  point.stringField('ScheduledDate', scheduledDate);

  influxWriter.send('FlightDateResolverFailCount', point);
};
